import type {
  SeasonalityKeyword,
  SeasonalityPoint,
  TrendKeyword,
  TrendOverview,
  TrendPoint,
  TrendSeasonalityPayload,
  TrendSeasonalityReportResponse,
} from '../models/trendSeasonality.types'

const EMPTY_OVERVIEW: TrendOverview = {
  total_monthly_observed_change: null,
  total_yearly_observed_change: null,
  total_monthly_observed_volume: null,
  total_yearly_observed_volume: null,
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function numeric(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function boolean(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
}

const ENGLISH_MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const

/** Accept the overlap wire shapes, then keep locale-sensitive names out of feature state. */
function calendarMonth(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 1 && value <= 12 ? value : null
  }
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  const englishIndex = ENGLISH_MONTHS.indexOf(normalized as (typeof ENGLISH_MONTHS)[number])
  if (englishIndex >= 0) return englishIndex + 1
  const numericMonth = /^\d{1,2}$/u.test(normalized) ? Number(normalized) : null
  if (numericMonth !== null && numericMonth >= 1 && numericMonth <= 12) return numericMonth
  const yearMonth = /^\d{4}-(\d{2})$/u.exec(normalized)
  if (!yearMonth) return null
  const parsed = Number(yearMonth[1])
  return parsed >= 1 && parsed <= 12 ? parsed : null
}

function reportRows(response: TrendSeasonalityReportResponse): unknown[] {
  return response.combined?.data ?? response.data ?? []
}

function overview(response: TrendSeasonalityReportResponse): TrendOverview {
  const source = record(response.combined?.other_data ?? response.other_data)
  if (!source) return { ...EMPTY_OVERVIEW }
  return {
    total_monthly_observed_change: numeric(source.total_monthly_observed_change),
    total_yearly_observed_change: numeric(source.total_yearly_observed_change),
    total_monthly_observed_volume: numeric(source.total_monthly_observed_volume),
    total_yearly_observed_volume: numeric(source.total_yearly_observed_volume),
  }
}

/** Group controller rows without mutating the wire payload (report 12 / BE-RPT-02). */
export function groupTrendRows(rows: readonly unknown[]): TrendKeyword[] {
  const grouped = new Map<string, TrendPoint[]>()
  for (const raw of rows) {
    const row = record(raw)
    const query = text(row?.search_query)
    if (!row || !query || !Array.isArray(row.breakdown_values)) continue
    for (const rawPoint of row.breakdown_values) {
      const point = record(rawPoint)
      const month = text(point?.month)
      if (!point || !month) continue
      const list = grouped.get(query) ?? []
      list.push({
        month,
        search_query: query,
        is_forecasted: boolean(row.is_forecasted),
        observed: numeric(point.observed),
        monthly_observed_change: numeric(point.monthly_observed_change),
        trend: numeric(point.trend),
        residual: numeric(point.residual),
      })
      grouped.set(query, list)
    }
  }
  return [...grouped].map(([query, data]) => ({
    query,
    data: [...data].sort((left, right) => left.month.localeCompare(right.month)),
  }))
}

export function groupSeasonalityRows(rows: readonly unknown[]): SeasonalityKeyword[] {
  const grouped = new Map<string, SeasonalityPoint[]>()
  for (const raw of rows) {
    const row = record(raw)
    const query = text(row?.search_query)
    const month = calendarMonth(row?.month)
    if (!row || !query || month === null) continue
    const list = grouped.get(query) ?? []
    list.push({ month, search_query: query, seasonal: numeric(row.value) })
    grouped.set(query, list)
  }
  return [...grouped].map(([query, data]) => ({
    query,
    data: [...data].sort((left, right) => left.month - right.month),
  }))
}

export function adaptTrendSeasonalityResponse(
  response: TrendSeasonalityReportResponse,
): TrendSeasonalityPayload {
  return {
    trends: groupTrendRows(reportRows(response)),
    seasonality: groupSeasonalityRows(response.trend_seasonality?.seasonality.data ?? []),
    overview: overview(response),
  }
}
